// Kangavena Web Widget Node - Client-side UI Component (process.env safe)
import React, { memo, useState, useMemo, useRef } from 'react';
import { Handle, Position, useReactFlow, useNodes, useEdges } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Globe,
  Code,
  Copy,
  Check,
  Shield,
  Sparkles,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Volume2,
  Mic,
  Palette,
  Layout,
  MessageSquare,
  Lock,
  Eye,
  CheckCircle2,
  AlertCircle,
  Upload,
  Loader2,
  RefreshCw,
  Monitor,
  Camera,
  Activity,
  Play
} from 'lucide-react';

export interface WebWidgetNodeData {
  isEnabled?: boolean;
  allowedDomains?: string;
  inheritCoreName?: boolean;
  overrideAgentName?: string;
  inheritCoreAvatar?: boolean;
  overrideAvatarUrl?: string;
  inheritCoreGreeting?: boolean;
  welcomeNote?: string;
  customSpeechTag?: string;
  autoOpenDelaySeconds?: number;
  embedType?: string;
  inheritCoreDirectives?: boolean;
  webPersonaDirectives?: string;
  themeColor?: string;
  position?: 'bottom-right' | 'bottom-left';
  voiceEnabled?: boolean;
  flightSoundEnabled?: boolean;
  enableScreenVision?: boolean;
  screenCaptureTriggerMode?: 'auto_on_demand' | 'always_on_start' | 'manual_icon_only';
  screenCaptureFps?: '0.5_fps' | '1_fps' | '2_fps' | 'on_speech';
  screenCaptureQuality?: '720p' | '1080p' | '480p';
  pdfAutoDetectEnabled?: boolean;
  privacyBadgeEnabled?: boolean;
  enableInteractivePointer?: boolean;
  enableLiveMouseTracking?: boolean;
  [key: string]: unknown;
}

export const WebWidgetNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();
  const nodeData = (data || {}) as WebWidgetNodeData;

  const isEnabled = nodeData.isEnabled !== false;
  const allowedDomains = nodeData.allowedDomains || '';
  const overrideAgentName = nodeData.overrideAgentName || '';
  const overrideAvatarUrl = nodeData.overrideAvatarUrl || '';
  const welcomeNote = nodeData.welcomeNote || '';
  const customSpeechTag = nodeData.customSpeechTag || '';
  const webPersonaDirectives = nodeData.webPersonaDirectives || '';
  const themeColor = nodeData.themeColor || '#06b6d4';
  const position = nodeData.position || 'bottom-right';
  const voiceEnabled = nodeData.voiceEnabled !== false;
  const flightSoundEnabled = nodeData.flightSoundEnabled !== false;
  const enableScreenVision = nodeData.enableScreenVision !== false;
  const screenCaptureTriggerMode = nodeData.screenCaptureTriggerMode || 'auto_on_demand';
  const screenCaptureFps = nodeData.screenCaptureFps || '1_fps';
  const screenCaptureQuality = nodeData.screenCaptureQuality || '720p';
  const pdfAutoDetectEnabled = nodeData.pdfAutoDetectEnabled !== false;
  const privacyBadgeEnabled = nodeData.privacyBadgeEnabled !== false;
  const enableInteractivePointer = nodeData.enableInteractivePointer !== false;
  const enableLiveMouseTracking = nodeData.enableLiveMouseTracking !== false;

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [embedTab, setEmbedTab] = useState<'html' | 'nextjs' | 'wordpress' | 'share'>('html');
  const [isPlaygroundOpen, setIsPlaygroundOpen] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  const { isTenantView, isWhiteLabelEnabled, tenant, authToken, tenantApiKey, pulseState, openPortalData, openAccountModal } = useTenantView();
  const isWhiteLabelActive = isWhiteLabelEnabled === true || tenant?.isWhiteLabelEnabled === true;
  const isCustomizable = (nodeData as any).isCustomizable !== false;
  const canCustomize = !isTenantView || isWhiteLabelActive || isCustomizable;

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, edges, nodes), [id, edges, nodes]);

  // Resolve connected Core Node data (for inherit display) with 100% reactive subscription to canvas nodes
  const connectedCoreData = useMemo(() => {
    const inboundEdge = edges.find(
      (e) => String(e.target) === String(id) && (e.targetHandle === 'in-web-widget' || !e.targetHandle)
    );
    if (inboundEdge) {
      const coreNode = nodes.find((n) => String(n.id) === String(inboundEdge.source));
      if (coreNode?.data) return coreNode.data as any;
    }
    // Resilient fallback: Primary Luna Core Hub on canvas
    const primaryCore = nodes.find((n) => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core');
    return primaryCore?.data as any;
  }, [id, edges, nodes]);

  const coreAgentName = connectedCoreData?.agentName || connectedCoreData?.customAvatarName || tenant?.customAvatarName || 'Luna AI';
  const coreAvatarUrl = connectedCoreData?.customAvatarUrl || tenant?.customAvatarUrl || '';

  const inheritCoreName = nodeData.inheritCoreName !== undefined 
    ? Boolean(nodeData.inheritCoreName) 
    : !Boolean(nodeData.overrideAgentName && nodeData.overrideAgentName !== coreAgentName);

  const inheritCoreAvatar = nodeData.inheritCoreAvatar !== undefined
    ? Boolean(nodeData.inheritCoreAvatar)
    : !Boolean(nodeData.overrideAvatarUrl && nodeData.overrideAvatarUrl !== coreAvatarUrl);

  const inheritCoreGreeting = nodeData.inheritCoreGreeting !== undefined
    ? Boolean(nodeData.inheritCoreGreeting)
    : !Boolean(nodeData.welcomeNote);

  const effectiveAgentName = inheritCoreName ? coreAgentName : (overrideAgentName || coreAgentName);
  const effectiveAvatarUrl = inheritCoreAvatar ? coreAvatarUrl : (overrideAvatarUrl || coreAvatarUrl);

  // Multi-format Embed Code Snippets
  const effectiveTenantKey = tenantApiKey || (nodeData as any)?.tenantId || 'YOUR_TENANT_API_KEY';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://kangavena.com';
  
  const htmlSnippet = `<script src="${origin}/luna-widget.js" data-api-key="${effectiveTenantKey}" data-agent-name="${effectiveAgentName}"${effectiveAvatarUrl ? ` data-avatar-url="${effectiveAvatarUrl}"` : ''} defer></script>`;
  const nextjsSnippet = `import Script from 'next/script';\n\n<Script\n  src="${origin}/luna-widget.js"\n  data-api-key="${effectiveTenantKey}"\n  data-agent-name="${effectiveAgentName}"\n  strategy="afterInteractive"\n/>`;
  const wordpressSnippet = `<!-- Kangavena Luna AI Co-Pilot (Header/Footer Script) -->\n<script src="${origin}/luna-widget.js" data-api-key="${effectiveTenantKey}" data-agent-name="${effectiveAgentName}" defer></script>`;
  const shareSimulatorUrl = `${origin}/demo.html?apiKey=${effectiveTenantKey}&tenantId=${effectiveTenantKey}`;

  const currentEmbedCode = embedTab === 'html' ? htmlSnippet : embedTab === 'nextjs' ? nextjsSnippet : embedTab === 'wordpress' ? wordpressSnippet : shareSimulatorUrl;

  const persistNodeDataDirectly = (allUpdates: Partial<WebWidgetNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          isEnabled,
          allowedDomains,
          inheritCoreName,
          overrideAgentName,
          inheritCoreAvatar,
          overrideAvatarUrl,
          inheritCoreGreeting,
          welcomeNote,
          customSpeechTag,
          autoOpenDelaySeconds: nodeData.autoOpenDelaySeconds,
          embedType: nodeData.embedType || 'floating_chibi',
          inheritCoreDirectives: nodeData.inheritCoreDirectives !== false,
          webPersonaDirectives,
          themeColor,
          position,
          voiceEnabled,
          flightSoundEnabled,
          enableScreenVision,
          screenCaptureTriggerMode,
          screenCaptureFps,
          screenCaptureQuality,
          pdfAutoDetectEnabled,
          privacyBadgeEnabled,
          enableInteractivePointer,
          enableLiveMouseTracking,
          ...allUpdates,
        };

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'WEB_WIDGET_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.error('[WebWidgetNode] Persist error:', err);
      }
    }, 400);
  };

  const handleFieldChange = (field: keyof WebWidgetNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly({ [field]: value });
  };

  const handleForceCoreSync = () => {
    const latestCore = nodes.find((n) => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core');
    const freshCoreData = latestCore?.data as any;
    const freshName = freshCoreData?.agentName || freshCoreData?.customAvatarName || tenant?.customAvatarName || 'Luna AI';
    const freshAvatar = freshCoreData?.customAvatarUrl || tenant?.customAvatarUrl || '';
    const newUpdates: Partial<WebWidgetNodeData> = {
      inheritCoreName: true,
      overrideAgentName: '',
      agentName: freshName,
      customAvatarName: freshName,
      inheritCoreAvatar: true,
      overrideAvatarUrl: '',
      customAvatarUrl: freshAvatar
    };
    setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...newUpdates } } : n)));
    persistNodeDataDirectly(newUpdates);
    setSaveIndicator('✅ Core Synced');
    setTimeout(() => setSaveIndicator(null), 2500);
  };

  const handleAvatarFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingAvatar(true);
    setAvatarUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers: Record<string, string> = {};
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers,
        body: formData,
      });
      const data = await res.json();
      if (data.success && data.url) {
        const fullUrl = data.url.startsWith('http') ? data.url : `${origin}${data.url}`;
        const newUpdates: Partial<WebWidgetNodeData> = {
          inheritCoreAvatar: false,
          overrideAvatarUrl: fullUrl,
          customAvatarUrl: fullUrl,
          avatarFaceId: 'custom_avatar'
        };
        setNodes((nds) =>
          nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...newUpdates } } : n))
        );
        persistNodeDataDirectly(newUpdates);
      } else {
        setAvatarUploadError(data.error || 'Upload failed');
      }
    } catch (err: any) {
      setAvatarUploadError(err?.message || 'Network error during upload');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
    }
  };

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[420px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#071520]/98 via-[#040D14]/98 to-[#02060A]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-cyan-400 ring-2 ring-cyan-400/50 shadow-[0_0_35px_rgba(6,182,212,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-cyan-500/40 hover:border-cyan-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Widget Live Telemetry in Portal ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto z-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openPortalData('web_widget', id);
          }}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-sky-950 border-2 border-cyan-400 text-cyan-200 text-[11px] font-mono font-bold tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:scale-105 hover:text-white transition-all backdrop-blur-md cursor-pointer whitespace-nowrap active:scale-95"
          title="Open Website Embed & Live Co-Pilot Studio in Portal Data"
        >
          <Globe size={12} className="text-cyan-400 animate-pulse" />
          <span>WEB CO-PILOT STUDIO</span>
          <ExternalLink size={10} className="text-cyan-400" />
        </button>
      </div>

      {/* ── Input Handle: Downstream Connection from Luna Core Hub ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-web-widget"
        className="!bg-cyan-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(6,182,212,1)]"
        title="Inbound: Connect from Luna Core Hub (out-web-widget or out-core)"
      />

      {/* ── Header ── */}
      <div className="p-4 border-b border-cyan-500/30 bg-gradient-to-r from-cyan-950/80 via-sky-950/60 to-slate-950/80 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
              <Globe size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white tracking-wide">Website Co-Pilot</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                  Web Embed
                </span>
                {saveIndicator && (
                  <span className="text-[10px] text-emerald-400 font-mono animate-fade-in font-bold">
                    {saveIndicator}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">Embeddable Live Voice & Chat Widget</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-8 h-4 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-cyan-500"></div>
            </label>
          </div>
        </div>

        {/* Wiring & Channel Status */}
        <div className="mt-3 flex items-center justify-between text-[11px] font-mono">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                isWired ? 'bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.8)]' : 'bg-amber-400 animate-pulse'
              }`}
            />
            <span className={isWired ? 'text-cyan-300' : 'text-amber-300'}>
              {isWired ? 'Wired to Luna Core Hub' : '⚠️ Connect to Luna Core Hub'}
            </span>
          </div>

          <span className="text-slate-400">
            {connectedCoreData ? `Core: ${connectedCoreData.agentName || 'Luna'}` : 'Standalone'}
          </span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-4 space-y-3.5 text-xs">
        {/* 1. Embed Snippet Box with Multi-Format Selector & 1-Click Copy */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="font-semibold text-slate-300 flex items-center gap-1.5 shrink-0">
              <Code size={13} className="text-cyan-400" />
              <span>Embed Integration Code</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(currentEmbedCode);
                  setCopiedSnippet(true);
                  setTimeout(() => setCopiedSnippet(false), 2500);
                }}
                className="px-2.5 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-400/40 text-[11px] font-bold font-sans flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95"
                title="Copy current snippet"
              >
                {copiedSnippet ? (
                  <>
                    <Check size={12} className="text-emerald-400" />
                    <span className="text-emerald-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    <span>Copy Code</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Format Tabs */}
          <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800 text-[10px] font-mono">
            <button
              type="button"
              onClick={() => setEmbedTab('html')}
              className={`flex-1 py-1 rounded-lg font-bold transition ${embedTab === 'html' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setEmbedTab('nextjs')}
              className={`flex-1 py-1 rounded-lg font-bold transition ${embedTab === 'nextjs' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Next.js
            </button>
            <button
              type="button"
              onClick={() => setEmbedTab('wordpress')}
              className={`flex-1 py-1 rounded-lg font-bold transition ${embedTab === 'wordpress' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              WordPress
            </button>
            <button
              type="button"
              onClick={() => setEmbedTab('share')}
              className={`flex-1 py-1 rounded-lg font-bold transition ${embedTab === 'share' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Simulator
            </button>
          </div>

          <div className="relative group/code">
            <pre className="p-3 rounded-xl bg-slate-900/90 border border-cyan-500/20 text-[11px] font-mono text-cyan-200 overflow-x-auto whitespace-pre-wrap break-all select-all max-h-24">
              {currentEmbedCode}
            </pre>
          </div>
        </div>

        {/* 2. Allowed Domains (Zero-Trust Security Whitelist) */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="font-semibold text-slate-300 flex items-center gap-1.5">
              <Shield size={13} className="text-emerald-400" />
              <span>Authorized Web Domains</span>
            </label>
            <span className="text-[10px] text-emerald-400 font-mono">Zero-Trust Shield</span>
          </div>
          <input
            type="text"
            value={allowedDomains}
            onChange={(e) => handleFieldChange('allowedDomains', e.target.value)}
            placeholder="e.g. sample.com, *.sample.com (comma separated)"
            className="w-full px-3 py-2 rounded-xl bg-slate-900/80 border border-slate-800 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white font-mono text-xs placeholder:text-slate-500 outline-none transition-all"
          />
          <p className="text-[10px] text-slate-400 leading-tight">
            Prevents third-party domain spoofing. Luna only initializes on authorized hosts.
          </p>
        </div>

        {/* 3. Web Agent Name & Avatar Sync (with Direct File Upload & White-Label Lock) */}
        <div className="grid grid-cols-2 gap-2.5">
          {/* Custom Web Name */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold text-slate-200">Web Agent Name</label>
              {!canCustomize && (
                <span className="text-[9px] font-mono text-amber-400 flex items-center gap-0.5">
                  <Lock size={9} /> Pro
                </span>
              )}
            </div>

            {/* Inherit Name Checkbox */}
            <label className="flex items-center gap-1.5 text-[10px] font-mono cursor-pointer select-none py-0.5">
              <input
                type="checkbox"
                checked={inheritCoreName}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const newUpdates: Partial<WebWidgetNodeData> = {
                    inheritCoreName: checked,
                    overrideAgentName: checked ? '' : (nodeData.overrideAgentName || coreAgentName),
                    agentName: checked ? coreAgentName : (nodeData.overrideAgentName || coreAgentName),
                    customAvatarName: checked ? coreAgentName : (nodeData.overrideAgentName || coreAgentName)
                  };
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...newUpdates } } : n))
                  );
                  persistNodeDataDirectly(newUpdates);
                }}
                className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30 accent-cyan-400 cursor-pointer"
              />
              <span className={inheritCoreName ? "text-emerald-400 font-bold" : "text-slate-400"}>
                Keep Luna Core Hub name
              </span>
            </label>

            <div className="relative">
              <input
                type="text"
                disabled={!canCustomize || inheritCoreName}
                value={inheritCoreName ? coreAgentName : (overrideAgentName || '')}
                onChange={(e) => {
                  const val = e.target.value;
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, overrideAgentName: val, agentName: val, customAvatarName: val } } : n))
                  );
                  persistNodeDataDirectly({ overrideAgentName: val, agentName: val, customAvatarName: val });
                }}
                placeholder={inheritCoreName ? `Inherited (${coreAgentName})` : "Enter custom website agent name"}
                className={`w-full px-2.5 py-1.5 rounded-lg border text-xs outline-none transition-all ${
                  inheritCoreName 
                    ? 'bg-slate-950/70 border-emerald-500/30 text-emerald-200 font-semibold cursor-not-allowed opacity-90'
                    : 'bg-slate-900/90 border-slate-800 focus:border-cyan-400 text-white'
                }`}
              />
              {inheritCoreName && (
                <button
                  type="button"
                  onClick={handleForceCoreSync}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[8px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 transition-all cursor-pointer shadow-sm active:scale-95 flex items-center gap-1 z-10"
                  title="Click to force re-sync name and avatar with Luna Core Hub"
                >
                  <RefreshCw size={9} />
                  CORE SYNC
                </button>
              )}
            </div>
          </div>

          {/* Web Avatar URL & Direct File Upload */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-bold text-slate-200">Web Avatar</label>
              {!canCustomize && (
                <button
                  type="button"
                  onClick={() => openAccountModal && openAccountModal()}
                  className="text-[9px] font-mono text-amber-400 hover:text-amber-300 flex items-center gap-0.5 cursor-pointer hover:underline"
                >
                  <Lock size={9} /> Pro
                </button>
              )}
            </div>

            {/* Inherit Avatar Checkbox */}
            <label className="flex items-center gap-1.5 text-[10px] font-mono cursor-pointer select-none py-0.5">
              <input
                type="checkbox"
                checked={inheritCoreAvatar}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const newUpdates: Partial<WebWidgetNodeData> = {
                    inheritCoreAvatar: checked,
                    overrideAvatarUrl: checked ? '' : (nodeData.overrideAvatarUrl || coreAvatarUrl),
                    customAvatarUrl: checked ? coreAvatarUrl : (nodeData.overrideAvatarUrl || coreAvatarUrl),
                    avatarFaceId: checked ? (connectedCoreData?.avatarFaceId || 'cute_chibi_bot') : 'custom_avatar'
                  };
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...newUpdates } } : n))
                  );
                  persistNodeDataDirectly(newUpdates);
                }}
                className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30 accent-cyan-400 cursor-pointer"
              />
              <span className={inheritCoreAvatar ? "text-emerald-400 font-bold" : "text-slate-400"}>
                Keep Luna Core Hub avatar
              </span>
            </label>

            <div className="flex items-center gap-1.5">
              {effectiveAvatarUrl ? (
                <img
                  src={effectiveAvatarUrl}
                  alt="Avatar"
                  className="w-7 h-7 rounded-full object-cover border border-cyan-400/40 shrink-0 bg-slate-900 shadow-[0_0_8px_rgba(6,182,212,0.4)]"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] text-cyan-400 shrink-0">
                  🤖
                </div>
              )}
              <input
                type="text"
                disabled={!canCustomize || inheritCoreAvatar}
                value={inheritCoreAvatar ? (coreAvatarUrl ? 'Core Photo / Logo' : 'Core Default Model') : (overrideAvatarUrl || '')}
                onChange={(e) => {
                  const val = e.target.value;
                  setNodes((nds) =>
                    nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, overrideAvatarUrl: val, customAvatarUrl: val } } : n))
                  );
                  persistNodeDataDirectly({ overrideAvatarUrl: val, customAvatarUrl: val });
                }}
                placeholder={inheritCoreAvatar ? "Inherited from Core Hub" : "Custom photo URL"}
                className={`w-full px-2 py-1.5 rounded-lg border text-[11px] font-mono outline-none transition-all truncate ${
                  inheritCoreAvatar
                    ? 'bg-slate-950/70 border-emerald-500/30 text-emerald-200 cursor-not-allowed opacity-90'
                    : 'bg-slate-900/90 border-slate-800 focus:border-cyan-400 text-white'
                }`}
              />
              <input
                type="file"
                ref={avatarFileInputRef}
                onChange={handleAvatarFileUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                disabled={!canCustomize || isUploadingAvatar || inheritCoreAvatar}
                onClick={() => avatarFileInputRef.current?.click()}
                className={`p-1.5 rounded-lg border transition cursor-pointer shrink-0 ${
                  inheritCoreAvatar
                    ? 'bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed opacity-40'
                    : 'bg-cyan-500/15 hover:bg-cyan-500/25 border-cyan-500/30 text-cyan-300 disabled:opacity-40'
                }`}
                title={inheritCoreAvatar ? "Uncheck 'Keep Luna Core Hub avatar' to upload a website-specific custom photo" : "Upload custom photo specifically for website"}
              >
                {isUploadingAvatar ? (
                  <Loader2 size={13} className="animate-spin text-cyan-400" />
                ) : (
                  <Upload size={13} />
                )}
              </button>
            </div>
            {avatarUploadError && (
              <p className="text-[10px] text-rose-400 leading-tight">{avatarUploadError}</p>
            )}
          </div>
        </div>

        {/* 4. Web Welcome Note */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-slate-200">Welcome Greeting Note</label>
            <span className="text-[10px] text-cyan-400/80 font-mono">Speech Tag: Speak with {effectiveAgentName}</span>
          </div>

          {/* Inherit Greeting Checkbox */}
          <label className="flex items-center gap-1.5 text-[10px] font-mono cursor-pointer select-none py-0.5">
            <input
              type="checkbox"
              checked={inheritCoreGreeting}
              onChange={(e) => {
                const checked = e.target.checked;
                const defaultGreeting = `ආයුබෝවන්, මම ${effectiveAgentName}. ඔබට සහය විය හැකි ආකාරය කියන්න.`;
                const newUpdates: Partial<WebWidgetNodeData> = {
                  inheritCoreGreeting: checked,
                  welcomeNote: checked ? '' : (nodeData.welcomeNote || defaultGreeting),
                  welcomeGreeting: checked ? '' : (nodeData.welcomeNote || defaultGreeting)
                };
                setNodes((nds) =>
                  nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...newUpdates } } : n))
                );
                persistNodeDataDirectly(newUpdates);
              }}
              className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-400/30 accent-cyan-400 cursor-pointer"
            />
            <span className={inheritCoreGreeting ? "text-emerald-400 font-bold" : "text-slate-400"}>
              Keep Luna Core Hub greeting & directives
            </span>
          </label>

          <input
            type="text"
            disabled={inheritCoreGreeting}
            value={inheritCoreGreeting ? `ආයුබෝවන්, මම ${effectiveAgentName}. ඔබට සහය විය හැකි ආකාරය කියන්න.` : welcomeNote}
            onChange={(e) => handleFieldChange('welcomeNote', e.target.value)}
            placeholder={`ආයුබෝවන්, මම ${effectiveAgentName}. ඔබට සහය විය හැකි ආකාරය කියන්න.`}
            className={`w-full px-2.5 py-1.5 rounded-lg border text-xs outline-none transition-all ${
              inheritCoreGreeting
                ? 'bg-slate-950/70 border-emerald-500/30 text-emerald-200 cursor-not-allowed opacity-90'
                : 'bg-slate-900/90 border-slate-800 focus:border-cyan-400 text-white'
            }`}
          />

          {/* Floating Speech Tag Pill */}
          <div className="pt-2 border-t border-slate-800/60 space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-semibold text-slate-300 flex items-center gap-1.5">
                <MessageSquare size={12} className="text-cyan-400" />
                <span>Floating Speech Tag Pill</span>
              </label>
              <span className="text-[10px] text-cyan-400/80 font-mono">Live Widget Badge</span>
            </div>
            <input
              type="text"
              value={customSpeechTag}
              onChange={(e) => handleFieldChange('customSpeechTag', e.target.value)}
              placeholder={`🎙️ Ask from ${effectiveAgentName}`}
              className="w-full px-2.5 py-1.5 rounded-lg border bg-slate-900/90 border-slate-800 focus:border-cyan-400 text-white text-xs outline-none transition-all placeholder:text-slate-500"
            />
            <p className="text-[10px] text-slate-500 leading-tight">
              Custom text displayed in the speech bubble pill beside the avatar (leave empty for default "🎙️ Ask from {effectiveAgentName}").
            </p>
          </div>
        </div>

        {/* 5. Multimodal Screen Vision & OCR Studio */}
        <div className="p-3 rounded-xl bg-gradient-to-r from-sky-950/40 via-cyan-950/30 to-slate-900/60 border border-cyan-500/30 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.3)]">
                <Monitor size={13} />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-white">Live Screen Vision</span>
                  <span className={`px-1.5 py-0.2 rounded text-[8px] font-mono font-bold uppercase ${
                    enableScreenVision ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}>
                    {enableScreenVision ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <span className="text-[10px] text-slate-400 block leading-tight">Luna Vision multimodal screen streaming for PDFs, notes & visual co-pilot</span>
              </div>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableScreenVision}
                onChange={(e) => handleFieldChange('enableScreenVision', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-8 h-4 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-cyan-500"></div>
            </label>
          </div>

          {enableScreenVision && (
            <div className="space-y-2.5 pt-2 border-t border-cyan-500/20 animate-fade-in text-[11px]">
              {/* Trigger Mode Selector */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-slate-300 font-semibold flex items-center gap-1">
                    <Eye size={11} className="text-cyan-400" />
                    <span>Vision Trigger Mode</span>
                  </label>
                  <span className="text-[9px] font-mono text-cyan-400">
                    {screenCaptureTriggerMode === 'auto_on_demand' ? 'Smart Auto' : screenCaptureTriggerMode === 'always_on_start' ? 'Auto-Prompt' : 'Manual'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 font-mono text-[10px]">
                  <button
                    type="button"
                    onClick={() => handleFieldChange('screenCaptureTriggerMode', 'auto_on_demand')}
                    className={`py-1.5 px-2 rounded-lg border text-center transition-all cursor-pointer ${
                      screenCaptureTriggerMode === 'auto_on_demand'
                        ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 font-bold shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                        : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Auto On-Demand
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFieldChange('screenCaptureTriggerMode', 'always_on_start')}
                    className={`py-1.5 px-2 rounded-lg border text-center transition-all cursor-pointer ${
                      screenCaptureTriggerMode === 'always_on_start'
                        ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 font-bold shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                        : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Always on Start
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFieldChange('screenCaptureTriggerMode', 'manual_icon_only')}
                    className={`py-1.5 px-2 rounded-lg border text-center transition-all cursor-pointer ${
                      screenCaptureTriggerMode === 'manual_icon_only'
                        ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 font-bold shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                        : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Manual Icon
                  </button>
                </div>
              </div>

              {/* FPS Frequency & Resolution Quality */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold flex items-center gap-1">
                    <Activity size={11} className="text-emerald-400" />
                    <span>Capture Frequency</span>
                  </label>
                  <select
                    value={screenCaptureFps}
                    onChange={(e) => handleFieldChange('screenCaptureFps', e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg bg-slate-900/90 border border-slate-800 text-white text-[11px] font-mono outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    <option value="1_fps">1 FPS (Balanced)</option>
                    <option value="0.5_fps">0.5 FPS (Eco Mode)</option>
                    <option value="2_fps">2 FPS (Smooth High-Speed)</option>
                    <option value="on_speech">On-Speech (Zero Idle)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-300 font-semibold flex items-center gap-1">
                    <Camera size={11} className="text-sky-400" />
                    <span>Resolution & Quality</span>
                  </label>
                  <select
                    value={screenCaptureQuality}
                    onChange={(e) => handleFieldChange('screenCaptureQuality', e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg bg-slate-900/90 border border-slate-800 text-white text-[11px] font-mono outline-none focus:border-cyan-400 cursor-pointer"
                  >
                    <option value="720p">720p HD (Optimal)</option>
                    <option value="1080p">1080p Full HD (Crisp)</option>
                    <option value="480p">480p (Low Bandwidth)</option>
                  </select>
                </div>
              </div>

              {/* Sub-toggles for PDF and Privacy Badge */}
              <div className="pt-1.5 border-t border-cyan-500/20 space-y-1.5">
                <label className="flex items-center justify-between cursor-pointer select-none py-0.5">
                  <span className="text-slate-300">Auto-Detect PDFs & Lesson Materials</span>
                  <input
                    type="checkbox"
                    checked={pdfAutoDetectEnabled}
                    onChange={(e) => handleFieldChange('pdfAutoDetectEnabled', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 accent-cyan-400 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer select-none py-0.5">
                  <span className="text-slate-300">Show Zero-Data Local Privacy Shield</span>
                  <input
                    type="checkbox"
                    checked={privacyBadgeEnabled}
                    onChange={(e) => handleFieldChange('privacyBadgeEnabled', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 accent-cyan-400 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer select-none py-0.5">
                  <div className="flex flex-col">
                    <span className="text-slate-300">Interactive Laser Pointer & Flight</span>
                    <span className="text-[9px] text-slate-500">Luna travels & highlights courses/cards on screen</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={enableInteractivePointer}
                    onChange={(e) => handleFieldChange('enableInteractivePointer', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 accent-cyan-400 cursor-pointer"
                  />
                </label>

                <label className="flex items-center justify-between cursor-pointer select-none py-0.5">
                  <div className="flex flex-col">
                    <span className="text-slate-300">Live Mouse Tracking & Focus Telemetry</span>
                    <span className="text-[9px] text-slate-500">Tracks visitor mouse hover & reading position</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={enableLiveMouseTracking}
                    onChange={(e) => handleFieldChange('enableLiveMouseTracking', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-cyan-500 accent-cyan-400 cursor-pointer"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* ── Advanced Web Customization Accordion ── */}
        <div className="pt-1 border-t border-slate-800/80">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="flex items-center justify-between w-full py-1.5 text-[11px] font-mono text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <span className="flex items-center gap-1.5 font-bold">
              <Palette size={13} />
              <span>ADVANCED WEB BEHAVIOR & THEME</span>
            </span>
            {isAdvancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {isAdvancedOpen && (
            <div className="space-y-3 pt-2 animate-fade-in text-xs">
              {/* Web Specific Behavioral Persona Directives */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-300 flex items-center gap-1">
                  <MessageSquare size={12} className="text-cyan-400" />
                  <span>Web Persona Directives (Website Interaction Only)</span>
                </label>
                <textarea
                  rows={2}
                  value={webPersonaDirectives}
                  onChange={(e) => handleFieldChange('webPersonaDirectives', e.target.value)}
                  placeholder="e.g. Guide online students and parents on curriculum, courses, and fees warmly as an online school teacher."
                  className="w-full px-2.5 py-2 rounded-xl bg-slate-900/80 border border-slate-800 focus:border-cyan-400 text-white text-xs placeholder:text-slate-500 outline-none resize-none"
                />
              </div>

              {/* Theme Accent Color & Floating Position */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-300">Accent Theme Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={themeColor}
                      onChange={(e) => handleFieldChange('themeColor', e.target.value)}
                      className="w-7 h-7 rounded-lg border-0 bg-transparent cursor-pointer"
                    />
                    <span className="font-mono text-xs text-slate-300 uppercase">{themeColor}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-300">Widget Position</label>
                  <select
                    value={position}
                    onChange={(e) => handleFieldChange('position', e.target.value as any)}
                    className="w-full px-2 py-1.5 rounded-lg bg-slate-900/80 border border-slate-800 text-white text-xs outline-none focus:border-cyan-400"
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                  </select>
                </div>
              </div>

              {/* Voice & Sound toggles */}
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/60">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={voiceEnabled}
                    onChange={(e) => handleFieldChange('voiceEnabled', e.target.checked)}
                    className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                  />
                  <span className="text-[11px] text-slate-300 flex items-center gap-1">
                    <Mic size={12} className="text-cyan-400" />
                    <span>Voice Enabled</span>
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={flightSoundEnabled}
                    onChange={(e) => handleFieldChange('flightSoundEnabled', e.target.checked)}
                    className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                  />
                  <span className="text-[11px] text-slate-300 flex items-center gap-1">
                    <Volume2 size={12} className="text-cyan-400" />
                    <span>Sci-Fi Sounds</span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="px-4 py-2.5 bg-slate-900/40 border-t border-slate-800/60 rounded-b-2xl flex items-center justify-between text-[11px] font-mono">
        <span className="text-slate-400 flex items-center gap-1">
          <Eye size={12} className="text-cyan-400" />
          <span>Active on {allowedDomains ? allowedDomains.split(',')[0] : 'All Domains'}</span>
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsPlaygroundOpen(true)}
            className="text-emerald-400 hover:text-emerald-300 hover:underline flex items-center gap-1 cursor-pointer font-sans font-bold"
            title="Open Interactive Canvas Simulator Playground"
          >
            <Play size={10} className="fill-emerald-400" />
            <span>Playground</span>
          </button>
          <button
            type="button"
            onClick={() => {
              const previewUrl = `${origin}/demo.html?apiKey=${effectiveTenantKey}&tenantId=${effectiveTenantKey}&t=${Date.now()}`;
              window.open(previewUrl, '_blank');
            }}
            className="text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1 cursor-pointer font-sans font-bold"
            title="Open live test widget in new window"
          >
            <span>Live Test</span>
            <ExternalLink size={10} />
          </button>
        </div>
      </div>

      {/* ── In-Canvas Interactive Test Playground Modal ── */}
      {isPlaygroundOpen && (
        <div
          className="fixed inset-0 z-[99999] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={() => setIsPlaygroundOpen(false)}
        >
          <div
            className="w-full max-w-5xl h-[88vh] bg-slate-950 border border-cyan-500/40 rounded-2xl shadow-[0_0_80px_rgba(6,182,212,0.4)] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-5 py-3.5 bg-slate-900/90 border-b border-cyan-500/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping"></span>
                <span className="font-extrabold text-white text-sm tracking-tight flex items-center gap-2">
                  <span>Interactive Co-Pilot Simulator</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                    {effectiveAgentName}
                  </span>
                </span>
                <span className="text-xs font-mono text-slate-500 hidden sm:inline">
                  Key: {effectiveTenantKey.slice(0, 14)}...
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const previewUrl = `${origin}/demo.html?apiKey=${effectiveTenantKey}&tenantId=${effectiveTenantKey}&t=${Date.now()}`;
                    window.open(previewUrl, '_blank');
                  }}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 flex items-center gap-1.5 transition"
                  title="Open Simulator in a separate browser tab"
                >
                  <ExternalLink size={12} className="text-cyan-400" />
                  <span>Open in Tab</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsPlaygroundOpen(false)}
                  className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center font-bold text-sm transition"
                  title="Close Playground"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Simulator Iframe */}
            <div className="flex-1 bg-slate-950 relative">
              <iframe
                src={`${origin}/demo.html?apiKey=${effectiveTenantKey}&tenantId=${effectiveTenantKey}&t=${Date.now()}`}
                className="w-full h-full border-0"
                allow="microphone *; autoplay *; camera *; display-capture *"
                title="Live Website Co-Pilot Simulator"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(WebWidgetNode);
